import { isFunction, get } from "lodash";
import React, { useState } from "react";
import Form from "antd/lib/form";
import Modal from "antd/lib/modal";
import Input from "antd/lib/input";
import { UserProfile } from "@/components/proptypes";
import { wrap as wrapDialog, DialogPropType } from "@/components/dialog-wrapper/DialogWrapper";
import User from "@/services/user";
import notification from "@/services/notification";
import { useTheme } from "@/components/ThemeProvider";

function ChangePasswordDialog({ user, dialog }) {
  const [state, setState] = useState({
    currentPassword: { value: "", error: null, touched: false },
    newPassword: { value: "", error: null, touched: false },
    repeatPassword: { value: "", error: null, touched: false },
    updatingPassword: false,
  });

  const { isDarkMode } = useTheme();

  const fieldError = (name, value) => {
    if (value.length === 0) return "This field is required.";
    if (name !== "currentPassword" && value.length < 6) return "This field is too short.";
    if (name === "repeatPassword" && value !== state.newPassword.value) return "Passwords don't match";
    return null;
  };

  const validateFields = callback => {
    const { currentPassword, newPassword, repeatPassword } = state;

    const errors = {
      currentPassword: fieldError("currentPassword", currentPassword.value),
      newPassword: fieldError("newPassword", newPassword.value),
      repeatPassword: fieldError("repeatPassword", repeatPassword.value),
    };

    setState(prevState => ({
      ...prevState,
      currentPassword: { ...currentPassword, error: errors.currentPassword },
      newPassword: { ...newPassword, error: errors.newPassword },
      repeatPassword: { ...repeatPassword, error: errors.repeatPassword },
    }));

    if (isFunction(callback)) {
      if (errors.currentPassword || errors.newPassword || errors.repeatPassword) {
        callback(errors);
      } else callback(null);
    }
  };

  const updatePassword = () => {
    const { currentPassword, newPassword, updatingPassword } = state;

    if (!updatingPassword) {
      validateFields(err => {
        if (!err) {
          const userData = {
            id: user.id,
            old_password: currentPassword.value,
            password: newPassword.value,
          };

          setState(prevState => ({ ...prevState, updatingPassword: true }));

          User.save(userData)
            .then(() => {
              notification.success("Saved.");
              dialog.close({ success: true });
            })
            .catch(error => {
              notification.error(get(error, "response.data.message", "Failed saving."));
              setState(prevState => ({ ...prevState, updatingPassword: false }));
            });
        } else {
          setState(prevState => ({
            ...prevState,
            currentPassword: { ...prevState.currentPassword, touched: true },
            newPassword: { ...prevState.newPassword, touched: true },
            repeatPassword: { ...prevState.repeatPassword, touched: true },
          }));
        }
      });
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    const { error } = state[name];

    setState(prevState => ({
      ...prevState,
      [name]: { value, error, touched: true },
    }), () => {
      validateFields();
    });
  };

  const { currentPassword, newPassword, repeatPassword, updatingPassword } = state;

  const formItemProps = { className: "m-b-10", required: true };

  const inputProps = {
    onChange: handleChange,
    onPressEnter: updatePassword,
  };

  return (
    <Modal
      {...dialog.props}
      okButtonProps={{ loading: updatingPassword }}
      onOk={updatePassword}
      title="Change Password"
      className={isDarkMode ? "dark-mode" : ""}>
      <Form layout="vertical">
        <Form.Item
          {...formItemProps}
          validateStatus={currentPassword.touched && currentPassword.error ? "error" : null}
          help={currentPassword.touched ? currentPassword.error : null}
          label="Current Password">
          <Input.Password {...inputProps} name="currentPassword" data-test="CurrentPassword" autoFocus />
        </Form.Item>
        <Form.Item
          {...formItemProps}
          validateStatus={newPassword.touched && newPassword.error ? "error" : null}
          help={newPassword.touched ? newPassword.error : null}
          label="New Password">
          <Input.Password {...inputProps} name="newPassword" data-test="NewPassword" />
        </Form.Item>
        <Form.Item
          {...formItemProps}
          validateStatus={repeatPassword.touched && repeatPassword.error ? "error" : null}
          help={repeatPassword.touched ? repeatPassword.error : null}
          label="Repeat New Password">
          <Input.Password {...inputProps} name="repeatPassword" data-test="RepeatPassword" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

ChangePasswordDialog.propTypes = {
  user: UserProfile.isRequired,
  dialog: DialogPropType.isRequired,
};

export default wrapDialog(ChangePasswordDialog);
